/* Tiny stale-while-revalidate cache for landing-page GET endpoints.
 * - Hydrates synchronously from sessionStorage so returning-visitors see content
 *   render on the first paint (no re-fetch flash).
 * - Kicks off a background revalidation to refresh the cache silently.
 * - TTL soft-caps stale data so a very old session doesn't pin bad payloads.
 */
import api from "@/lib/api";

const KEY = (path) => `swr:${path}`;
const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 min soft-freshness window

export function readCache(path) {
  try {
    const raw = sessionStorage.getItem(KEY(path));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !("data" in parsed)) return null;
    return parsed; // { data, ts }
  } catch {
    return null;
  }
}

export function writeCache(path, data) {
  try {
    sessionStorage.setItem(KEY(path), JSON.stringify({ data, ts: Date.now() }));
  } catch {
    /* quota exceeded — silently ignore */
  }
}

/* Hook-friendly helper: returns { data, revalidate } where `data` is either
 * the cached snapshot (may be null on very first visit) or the latest fetch.
 * The caller stores it in state; revalidation updates that state on completion. */
export async function fetchWithCache(path, onFresh, { ttl = DEFAULT_TTL_MS } = {}) {
  const cached = readCache(path);
  const isFresh = cached && Date.now() - cached.ts < ttl;

  // If we have any cached data (fresh or stale), consumer has already rendered it.
  // Only hit the network when stale/absent OR when the caller explicitly wants revalidation.
  if (isFresh) return cached.data;

  try {
    const res = await api.get(path);
    writeCache(path, res.data);
    if (typeof onFresh === "function") onFresh(res.data);
    return res.data;
  } catch (err) {
    if (cached) return cached.data; // fall back to stale on network failure
    throw err;
  }
}
